import React, { useEffect, useState, useMemo, useRef } from "react";
import {
    Button,
    Card,
    Col,
    Form,
    Row,
    ProgressBar,
    Modal,
} from "react-bootstrap";
import BroadcastService from "../../services/BroadcastService";
import WhatsAppTemplateService from "../../services/WhatsAppTemplateService";
import { API_STATUS } from '../../constants/apiStatusCode';
import { FaPaperPlane, FaCheckCircle } from "react-icons/fa";
import { GrView } from "react-icons/gr";
import { BroadcastStatus } from "../../constants/whatsAppConstants";
import GroupService from "../../services/GroupService";
import LeadService from "../../services/LeadService";

// Persist throttle timestamps and cached data across component unmounts
// (e.g. when switching tabs).
let lastTemplatesCallTs = 0;
let lastGroupsCallTs = 0;
let lastLeadsCallTs = 0;
let lastHistoryCallTs = 0;
let lastHistoryPageGlobal = 0;

let cachedTemplates = [];
let cachedGroups = [];
let cachedLeads = [];
let cachedBroadcastHistory = [];
let cachedHistoryTotalCount = 0;

const PAGE_SIZE = 5;
const API_THROTTLE_TIME = 5 * 60 * 1000; // 5 minutes in milliseconds

const Broadcast = () => {
    // Initialise state from cached values so that when we throttle API calls
    // after tab changes, the previously loaded data is still shown.
    const [templates, setTemplates] = useState(() => cachedTemplates);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [templateParameters, setTemplateParameters] = useState([]);
    const [placeholderNumbers, setPlaceholderNumbers] = useState([]);
    const [recipientType, setRecipientType] = useState("group"); // "group" or "lead"
    const [groups, setGroups] = useState(() => cachedGroups);
    const [selectedGroupIds, setSelectedGroupIds] = useState([]);
    const [leads, setLeads] = useState(() => cachedLeads);
    const [selectedLeadId, setSelectedLeadId] = useState("");
    const [broadcastHistory, setBroadcastHistory] = useState(() => cachedBroadcastHistory);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [languageCode, setLanguageCode] = useState("en");
    const [historyPage, setHistoryPage] = useState(1);
    const [historyTotalCount, setHistoryTotalCount] = useState(() => cachedHistoryTotalCount);
    const [historySearch, setHistorySearch] = useState("");
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewBroadcast, setViewBroadcast] = useState(null);

    // Throttling refs to track last API call times and last page.
    // Initialise from module-level variables so values persist across tab changes
    // (component unmounts / remounts).
    const lastTemplatesCall = useRef(lastTemplatesCallTs);
    const lastGroupsCall = useRef(lastGroupsCallTs);
    const lastLeadsCall = useRef(lastLeadsCallTs);
    const lastHistoryCall = useRef(lastHistoryCallTs);
    const lastHistoryPage = useRef(lastHistoryPageGlobal);

    const historyTotalPages = useMemo(
        () => (historyTotalCount ? Math.ceil(historyTotalCount / PAGE_SIZE) : 1),
        [historyTotalCount]
    );

    // Fetch templates
    useEffect(() => {
        fetchTemplates();
        fetchGroups();
        fetchLeads();
    }, []);

    // Fetch broadcast history when page changes
    useEffect(() => {
        fetchBroadcastHistory();
    }, [historyPage]);

    // Debounced search effect
    useEffect(() => {
        const searchTimeout = setTimeout(() => {
            setHistoryPage(1);
        }, 400);

        return () => clearTimeout(searchTimeout);
    }, [historySearch]);

    const fetchTemplates = async (force = false) => {
        const now = Date.now();
        if (!force && (now - lastTemplatesCall.current) < API_THROTTLE_TIME) {
            console.log("Templates API call throttled - last call was less than 5 minutes ago");
            return;
        }

        try {
            setLoading(true);
            lastTemplatesCall.current = now;
            lastTemplatesCallTs = now;
            const resp = await WhatsAppTemplateService.getApprovedTemplates();
            const data = resp?.data?.data || [];
            setTemplates(data);
            cachedTemplates = data;
        } catch (e) {
            console.error("Failed to load templates", e);
        } finally {
            setLoading(false);
        }
    };

    const fetchGroups = async (force = false) => {
        const now = Date.now();
        if (!force && (now - lastGroupsCall.current) < API_THROTTLE_TIME) {
            console.log("Groups API call throttled - last call was less than 5 minutes ago");
            return;
        }

        try {
            lastGroupsCall.current = now;
            lastGroupsCallTs = now;
            const resp = await GroupService.getGroups();
            const data = resp?.data?.data  || [];
            setGroups(data);
            cachedGroups = data;
        } catch (e) {
            console.error("Failed to load groups", e);
        }
    };

    const fetchLeads = async (force = false) => {
        const now = Date.now();
        if (!force && (now - lastLeadsCall.current) < API_THROTTLE_TIME) {
            console.log("Leads API call throttled - last call was less than 5 minutes ago");
            return;
        }

        try {
            lastLeadsCall.current = now;
            lastLeadsCallTs = now;
            const resp = await LeadService.getLeadsInfo();
            const data = resp?.data?.data || [];
            setLeads(data);
            cachedLeads = data;
        } catch (e) {
            console.error("Failed to load leads", e);
        }
    };

    const fetchBroadcastHistory = async (page = null, search = null, force = false) => {
        const now = Date.now();
        const currentPage = page !== null ? page : historyPage;
        // Only throttle if it's the same page and within throttle time
        const isSamePage = lastHistoryPage.current === currentPage;
        if (!force && isSamePage && (now - lastHistoryCall.current) < API_THROTTLE_TIME) {
            console.log("Broadcast history API call throttled - last call was less than 5 minutes ago");
            return;
        }

        try {
            setLoadingHistory(true);
            lastHistoryCall.current = now;
            lastHistoryPage.current = currentPage;
            lastHistoryCallTs = now;
            lastHistoryPageGlobal = currentPage;
            const params = {
                PageNumber: currentPage,
                PageSize: PAGE_SIZE,
                Search: (search !== null ? search : historySearch) || undefined,
            };

            const resp = await BroadcastService.getBroadcastHistory(params);
            const data = resp?.data?.data;
            const items = data?.items || [];
            setBroadcastHistory(items);
            setHistoryTotalCount(data?.totalCount || items.length);

            cachedBroadcastHistory = items;
            cachedHistoryTotalCount = data?.totalCount || items.length;
        } catch (e) {
            console.error("Failed to load broadcast history", e);
            setBroadcastHistory([]);
            setHistoryTotalCount(0);
        } finally {
            setLoadingHistory(false);
        }
    };

    // Extract placeholders from template body (e.g., {{1}}, {{2}})
    const extractPlaceholders = (templateBody) => {
        if (!templateBody) return [];
        const matches = templateBody.match(/\{\{(\d+)\}\}/g);
        if (!matches) return [];
        const placeholders = matches.map(m => {
            const num = m.match(/\d+/)[0];
            return parseInt(num);
        });
        return [...new Set(placeholders)].sort((a, b) => a - b);
    };

    // Handle template selection
    const handleTemplateSelect = (templateId) => {
        const template = templates.find(t => t.id === templateId || t.metaTemplateId === templateId);
        if (template) {
            setSelectedTemplate(template);
            const placeholders = extractPlaceholders(template.bodyText || "");
            setPlaceholderNumbers(placeholders);
            setTemplateParameters(placeholders.map(() => ""));
            setLanguageCode(template.language || "en_US");
        } else {
            setSelectedTemplate(null);
            setTemplateParameters([]);
            setPlaceholderNumbers([]);
        }
    };

    // Handle parameter change
    const handleParameterChange = (index, value) => {
        const newParams = [...templateParameters];
        newParams[index] = value;
        setTemplateParameters(newParams);
    };

    // Handle group checkbox toggle
    const handleGroupToggle = (groupId) => {
        setSelectedGroupIds(prev => {
            if (prev.includes(groupId)) {
                return prev.filter(id => id !== groupId);
            } else {
                return [...prev, groupId];
            }
        });
    };

    // Build template preview with parameters
    const buildTemplatePreview = () => {
        if (!selectedTemplate) return "";
        let preview = selectedTemplate.bodyText || "";
        placeholderNumbers.forEach((placeholderNum, index) => {
            const placeholder = `{{${placeholderNum}}}`;
            const paramValue = templateParameters[index] || placeholder;
            preview = preview.replace(new RegExp(`\\{\\{${placeholderNum}\\}\\}`, 'g'), paramValue);
        });
        return preview;
    };

    // Calculate expected recipients
    const calculateExpectedRecipients = () => {
        if (recipientType === "group") {
            return groups
                .filter(g => selectedGroupIds.includes(g.groupId || g.id))
                .reduce((sum, g) => sum + (g.contactCount || g.contacts?.length || 0), 0);
        } else {
            return selectedLeadId ? 1 : 0;
        }
    };

    // Send broadcast
    const handleSendBroadcast = async () => {
        if (!selectedTemplate) {
            alert("Please select a template");
            return;
        }

        if (recipientType === "group") {
            if (selectedGroupIds.length === 0) {
                alert("Please select at least one group");
                return;
            }
        } else {
            if (!selectedLeadId) {
                alert("Please select a lead");
                return;
            }
        }

        // Validate all parameters are filled
        if (placeholderNumbers.length > 0 && templateParameters.some(p => !p.trim())) {
            alert("Please fill in all template parameters");
            return;
        }

        try {
            setSending(true);
            const payload = {
                templateName: selectedTemplate.name,
                languageCode: languageCode,
                templateParameters: templateParameters.filter(p => p.trim()),
                groupIds: selectedGroupIds || [],
                leadId: selectedLeadId || "293f6e2e-5e62-42de-87a1-ab94c815c666",
                expectedRecipients: calculateExpectedRecipients(),
            };

            let resp;
            if (recipientType === "group") {
                resp = await BroadcastService.broacastMessage(payload);
            } else {
                resp = await BroadcastService.sendMessage(payload);
            }
            if (resp?.data?.success) {
                // Reset form
                setSelectedTemplate(null);
                setTemplateParameters([]);
                setSelectedGroupIds([]);
                setSelectedLeadId("");
                // Reset to page 1 and clear search
                setHistoryPage(1);
                setHistorySearch("");
                // Force refresh after broadcast (bypass throttle)
                fetchBroadcastHistory(1, "", true);
                alert(resp?.data?.message || "Broadcast sent successfully");
            } else {
                alert(resp?.data?.message || "Failed to send broadcast");
            }
        } catch (e) {
            console.error("Failed to send broadcast", e);
            alert(e?.response?.data?.message || "Failed to send broadcast");
        } finally {
            setSending(false);
        }
    };

    // Format date and time
    const formatDateTime = (dateString) => {
        if (!dateString) return "";
        try {
            const date = new Date(dateString);
            const dateStr = date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            });
            const timeStr = date.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
            });
            return `${dateStr} ${timeStr}`;
        } catch (e) {
            return dateString;
        }
    };

    // Map integer status code to status string
    const getBroadcastStatusText = (statusCode) => {
        if (statusCode === BroadcastStatus.Pending) return "Pending";
        if (statusCode === BroadcastStatus.Running) return "Running";
        if (statusCode === BroadcastStatus.Completed) return "Completed";
        if (statusCode === BroadcastStatus.PartiallyFailed) return "Partially Failed";
        if (statusCode === BroadcastStatus.Failed) return "Failed";
        return "Unknown";
    };

    // Check if broadcast is completed
    const isBroadcastCompleted = (statusCode) => {
        return statusCode === BroadcastStatus.Completed;
    };

    // Get status variant for display
    const getStatusVariant = (statusCode) => {
        if (statusCode === BroadcastStatus.Completed) return "success";
        if (statusCode === BroadcastStatus.Running) return "primary";
        if (statusCode === BroadcastStatus.PartiallyFailed) return "warning";
        if (statusCode === BroadcastStatus.Failed) return "danger";
        return "secondary";
    };

    // Handle search change
    const handleHistorySearchChange = (e) => {
        setHistorySearch(e.target.value);
    };

    // Handle search submit
    const handleHistorySearchSubmit = (e) => {
        e.preventDefault();
        setHistoryPage(1);
        // Force refresh when user explicitly searches
        fetchBroadcastHistory(1, historySearch, true);
    };

    // Open view modal
    const openViewModal = (broadcast) => {
        setViewBroadcast(broadcast);
        setShowViewModal(true);
    };

    const templatePreview = buildTemplatePreview();

    return (
        <div className="py-3">
            <Row className="mb-3">
                <Col>
                    <h3 className="fw-bold">Broadcast Messages</h3>
                    <div className="text-muted">
                        Send approved templates to contact groups via WhatsApp Business API.
                    </div>
                </Col>
            </Row>

            <Row className="g-4">
                {/* Left Panel - Create Broadcast */}
                <Col md={6}>
                    <Card className="h-100">
                        <Card.Body>
                            <h5 className="fw-bold mb-4">Create Broadcast</h5>

                            {/* Select Template */}
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    Select Template <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Select
                                    value={selectedTemplate?.id || selectedTemplate?.metaTemplateId || ""}
                                    onChange={(e) => handleTemplateSelect(e.target.value)}
                                    disabled={loading}
                                >
                                    <option value="">Choose a template...</option>
                                    {templates.map((template) => (
                                        <option
                                            key={template.id || template.metaTemplateId}
                                            value={template.id || template.metaTemplateId}
                                        >
                                            {template.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>

                            {/* Template Preview */}
                            {selectedTemplate && (
                                <>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Template Preview</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            value={templatePreview}
                                            readOnly
                                            style={{ backgroundColor: "#f8f9fa" }}
                                        />
                                    </Form.Group>

                                    {/* Template Parameters */}
                                    {placeholderNumbers.length > 0 && (
                                        <Form.Group className="mb-3">
                                            {placeholderNumbers.map((num, index) => (
                                                <Form.Group key={num} className="mb-2">
                                                    <Form.Label>Parameter {`{{${num}}}`}</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={templateParameters[index] || ""}
                                                        onChange={(e) => handleParameterChange(index, e.target.value)}
                                                        placeholder={`Enter value for {{${num}}}`}
                                                    />
                                                </Form.Group>
                                            ))}
                                        </Form.Group>
                                    )}
                                </>
                            )}

                            {/* Radio Buttons for Recipient Type */}
                            <Form.Group className="mb-3">
                                <Form.Label className="d-block mb-2">Select Recipient Type</Form.Label>
                                <div className="d-flex gap-4">
                                    <Form.Check
                                        type="radio"
                                        id="recipient-group"
                                        label="Send to Group"
                                        checked={recipientType === "group"}
                                        onChange={() => {
                                            setRecipientType("group");
                                            setSelectedLeadId("");
                                        }}
                                    />
                                    <Form.Check
                                        type="radio"
                                        id="recipient-lead"
                                        label="Send to Lead"
                                        checked={recipientType === "lead"}
                                        onChange={() => {
                                            setRecipientType("lead");
                                            setSelectedGroupIds([]);
                                        }}
                                    />
                                </div>
                            </Form.Group>

                            {/* Groups Selection */}
                            {recipientType === "group" && (
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Select Groups <span className="text-danger">*</span>
                                    </Form.Label>
                                    <div className="border rounded p-3" style={{ maxHeight: "200px", overflowY: "auto" }}>
                                        {groups.length === 0 ? (
                                            <div className="text-muted text-center py-2">No groups available</div>
                                        ) : (
                                            groups.map((group) => (
                                                <Form.Check
                                                    key={group.groupId || group.id}
                                                    type="checkbox"
                                                    id={`group-${group.groupId || group.id}`}
                                                    label={
                                                        <div>
                                                            <div className="fw-bold">{group.name}</div>
                                                            <div className="text-muted small">
                                                                {group.contactCount || group.contacts?.length || 0} contacts
                                                            </div>
                                                        </div>
                                                    }
                                                    checked={selectedGroupIds.includes(group.groupId || group.id)}
                                                    onChange={() => handleGroupToggle(group.groupId || group.id)}
                                                    className="mb-2"
                                                />
                                            ))
                                        )}
                                    </div>
                                </Form.Group>
                            )}

                            {/* Leads Selection */}
                            {recipientType === "lead" && (
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Select Lead <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        value={selectedLeadId}
                                        onChange={(e) => setSelectedLeadId(e.target.value)}
                                    >
                                        <option value="">Choose a lead...</option>
                                        {leads.map((lead) => (
                                            <option
                                                key={lead.leadId || lead.id}
                                                value={lead.leadId || lead.id}
                                            >
                                                {lead.name || lead.leadName || `Lead ${lead.leadId || lead.id}`}
                                                {lead.phoneNumber ? ` - ${lead.phoneNumber}` : ""}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            )}

                            {/* Send Button */}
                            <Button
                                variant="primary"
                                className="w-100 d-inline-flex align-items-center justify-content-center flex-nowrap gap-2"
                                style={{ whiteSpace: "nowrap" }}
                                onClick={handleSendBroadcast}
                                disabled={sending || !selectedTemplate}
                            >
                                <FaPaperPlane className="flex-shrink-0" size={14} aria-hidden />
                                <span>{sending ? "Sending..." : "Send Broadcast"}</span>
                            </Button>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Right Panel - Broadcast History */}
                <Col md={6}>
                    <Card className="h-100">
                        <Card.Body>
                            <h5 className="fw-bold mb-4">Broadcast History</h5>

                            {/* Search Form */}
                            <Form onSubmit={handleHistorySearchSubmit} className="mb-3">
                                <Form.Control
                                    type="text"
                                    title="Press Enter to search"
                                    placeholder="Search broadcast history..."
                                    value={historySearch}
                                    onChange={handleHistorySearchChange}
                                />
                            </Form>

                            {loadingHistory && (
                                <div className="text-center text-muted py-4">Loading...</div>
                            )}

                            {!loadingHistory && broadcastHistory.length === 0 && (
                                <div className="text-center text-muted py-4">
                                    No broadcast history available
                                </div>
                            )}

                            {!loadingHistory && broadcastHistory.length > 0 && (
                                <>
                                    <div className="d-flex flex-column gap-3">
                                        {broadcastHistory.map((broadcast, index) => {
                                            const sentCount = broadcast.sentCount || 0;
                                            const totalCount = broadcast.totalRecipients || 0;
                                            const percentage = totalCount > 0 ? (sentCount / totalCount) * 100 : 0;
                                            const statusCode = typeof broadcast.status === 'number' ? broadcast.status : BroadcastStatus.Pending;
                                            const statusText = getBroadcastStatusText(statusCode);
                                            const isCompleted = isBroadcastCompleted(statusCode);
                                            const statusVariant = getStatusVariant(statusCode);

                                            return (
                                                <Card key={broadcast.id || index} className="border">
                                                    <Card.Body>
                                                        <div className="d-flex align-items-start gap-3">
                                                            {/* Checkmark Icon on Left */}
                                                            {isCompleted && (
                                                                <div className="mt-1">
                                                                    <FaCheckCircle className="text-success" style={{ fontSize: "20px" }} />
                                                                </div>
                                                            )}
                                                            
                                                            <div className="flex-grow-1">
                                                                {/* Template Name */}
                                                                <div className="mb-1">
                                                                    <h6 className="mb-0 fw-bold">{broadcast.templateName}</h6>
                                                                </div>
                                                                
                                                                {/* Recipient Group */}
                                                                <div className="text-muted small mb-2">
                                                                    {broadcast.targetGroupNames || "N/A"}
                                                                </div>
                                                                
                                                                {/* Status with Sent Count */}
                                                                <div className="mb-2">
                                                                    <span className={`small text-${statusVariant} fw-bold`}>
                                                                        Sent: {sentCount}/{totalCount} {isCompleted && "✓"} {statusText}
                                                                    </span>
                                                                </div>
                                                                
                                                                {/* Progress Bar */}
                                                                <ProgressBar
                                                                    now={percentage}
                                                                    variant={statusVariant}
                                                                    className="mb-0"
                                                                    style={{ height: "8px" }}
                                                                />
                                                            </div>
                                                            
                                                            {/* Date/Time and View Icon on Right */}
                                                            <div className="d-flex align-items-start gap-2">
                                                                <div className="text-muted small text-end">
                                                                    {formatDateTime(broadcast.createdOn)}
                                                                </div>
                                                                <Button
                                                                    variant="link"
                                                                    size="sm"
                                                                    className="p-0"
                                                                    onClick={() => openViewModal(broadcast)}
                                                                    title="View Details"
                                                                >
                                                                    <GrView />
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            );
                                        })}
                                    </div>

                                    {/* Pagination Controls */}
                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                        <div className="text-muted small">
                                            Page {historyPage} of {historyTotalPages}
                                        </div>
                                        <div className="d-flex gap-2">
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                disabled={historyPage <= 1}
                                                onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                                            >
                                                Previous
                                            </Button>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                disabled={historyPage >= historyTotalPages}
                                                onClick={() =>
                                                    setHistoryPage((p) =>
                                                        historyTotalPages
                                                            ? Math.min(historyTotalPages, p + 1)
                                                            : p + 1
                                                    )
                                                }
                                            >
                                                Next
                                            </Button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* View Broadcast Modal */}
            <Modal
                show={showViewModal}
                onHide={() => setShowViewModal(false)}
                centered
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        Broadcast Details
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {viewBroadcast && (() => {
                        const sentCount = viewBroadcast.sentCount || 0;
                        const totalCount = viewBroadcast.totalRecipients || 0;                       
                        let messageContent = viewBroadcast.templateContent || "Message content not available"; 
                        const isCompleted = isBroadcastCompleted(viewBroadcast.status);
                               
                        // Get time from createdOn
                        const messageTime = viewBroadcast.createdOn 
                            ? new Date(viewBroadcast.createdOn).toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true
                            })
                            : "";

                        return (
                            <>
                                {/* Template Name */}
                                <div className="mb-4">
                                    <div className="text-muted small mb-1" style={{ textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                        TEMPLATE NAME
                                    </div>
                                    <div className="fw-bold" style={{ fontSize: "16px" }}>
                                        {viewBroadcast.templateName || "N/A"}
                                    </div>
                                </div>

                                {/* Recipients */}
                                <div className="mb-4">
                                    <div className="text-muted small mb-1" style={{ textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                        RECIPIENTS
                                    </div>
                                    <div className="fw-bold mb-1" style={{ fontSize: "16px" }}>
                                        {viewBroadcast.targetGroupNames || "N/A"}
                                    </div>
                                    <div className="text-muted small">
                                        {sentCount} of {totalCount} sent successfully
                                    </div>
                                </div>

                                {/* Message Content */}
                                <div className="mb-3">
                                    <div className="text-muted small mb-2" style={{ textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                        MESSAGE CONTENT
                                    </div>
                                    <div 
                                        style={{
                                            backgroundColor: "#dcf8c6",
                                            borderRadius: "8px",
                                            padding: "12px 16px",
                                            maxWidth: "85%",
                                            marginBottom: "4px",
                                            position: "relative"
                                        }}
                                    >
                                        <div style={{ fontSize: "14px", lineHeight: "1.4", marginBottom: "4px" }}>
                                            {messageContent?.split("\n").map((line, i) => (
                                                <div key={i}>{line}</div>
                                            ))}
                                        </div>
                                        <div className="d-flex align-items-center justify-content-end gap-1" style={{ fontSize: "12px", marginTop: "4px" }}>
                                            <span style={{ color: "#666" }}>
                                                {messageTime}
                                            </span>
                                            {isCompleted && (
                                                <span style={{ color: "#25D366", fontSize: "14px" }}>
                                                    ✓✓
                                                </span>
                                            )}
                                            {!isCompleted && (
                                                <span style={{ fontSize: "14px" }}>
                                                    ⚠️
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        );
                    })()}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowViewModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default Broadcast;
